import { DatePipe } from '@angular/common';
import { Component, computed, inject, signal } from '@angular/core';
import { FormsModule } from '@angular/forms';
import {
  AdminButtonComponent,
  AdminEmptyStateComponent,
  AdminModalComponent,
  AdminPageHeaderComponent,
  AdminTableComponent,
  StatusBadgeComponent,
} from '../../../shared/admin';
import { PlatformUserDto, PlatformUsersService } from '../../../shared/services/platform-users.service';
import { AuthService } from '../../../services/auth.service';
import { PermissionService } from '../../permissions/permission.service';

@Component({
  selector: 'app-users-page',
  standalone: true,
  imports: [
    DatePipe,
    FormsModule,
    AdminPageHeaderComponent,
    AdminButtonComponent,
    AdminTableComponent,
    AdminEmptyStateComponent,
    AdminModalComponent,
    StatusBadgeComponent,
  ],
  templateUrl: './users-page.component.html',
})
export class UsersPageComponent {
  private readonly usersService = inject(PlatformUsersService);
  private readonly authService = inject(AuthService);
  private readonly permissions = inject(PermissionService);

  readonly users = signal<PlatformUserDto[]>([]);
  readonly createModalOpen = signal(false);
  readonly createSaving = signal(false);
  readonly createError = signal<string | null>(null);
  readonly editingUser = signal<PlatformUserDto | null>(null);
  readonly editSaving = signal(false);
  readonly editError = signal<string | null>(null);

  readonly showCreate = computed(() => this.permissions.canShow('staff.platformUsers', 'create'));
  readonly canCreate = computed(() => this.permissions.canExecute('staff.platformUsers', 'create'));
  readonly showEdit = computed(() => this.permissions.canShow('staff.platformUsers', 'edit'));
  readonly canEdit = computed(() => this.permissions.canExecute('staff.platformUsers', 'edit'));
  readonly showDelete = computed(() => this.permissions.canShow('staff.platformUsers', 'delete'));
  readonly canDeleteAction = computed(() => this.permissions.canExecute('staff.platformUsers', 'delete'));
  readonly showActionsColumn = computed(
    () => this.showEdit() || this.showDelete(),
  );
  readonly currentUserId = computed(() => this.authService.getCurrentUser()?.userId);

  createName = '';
  createEmail = '';
  createRoleKey: 'SUPER_ADMIN' | 'PLATFORM_STAFF' | 'COUNSELLOR' = 'PLATFORM_STAFF';
  editName = '';
  editRoleKey: 'SUPER_ADMIN' | 'PLATFORM_STAFF' | 'COUNSELLOR' = 'PLATFORM_STAFF';
  readonly roleOptions = [
    { value: 'PLATFORM_STAFF' as const, label: 'PLATFORM_STAFF' },
    { value: 'COUNSELLOR' as const, label: 'COUNSELLOR' },
    { value: 'SUPER_ADMIN' as const, label: 'SUPER_ADMIN' },
  ];

  constructor() {
    this.refresh();
  }

  refresh(): void {
    this.usersService.list().subscribe((items) => this.users.set(items));
  }

  openCreate(): void {
    if (!this.canCreate()) return;
    this.createName = '';
    this.createEmail = '';
    this.createRoleKey = 'PLATFORM_STAFF';
    this.createError.set(null);
    this.createModalOpen.set(true);
  }

  closeCreate(): void {
    if (this.createSaving()) return;
    this.createModalOpen.set(false);
  }

  confirmCreate(): void {
    if (!this.canCreate()) return;
    const name = this.createName.trim();
    const email = this.createEmail.trim().toLowerCase();
    if (!name || !email) {
      this.createError.set('Name and email are required.');
      return;
    }

    this.createSaving.set(true);
    this.createError.set(null);
    this.usersService.create({ name, email, roleKey: this.createRoleKey }).subscribe({
      next: () => {
        this.createSaving.set(false);
        this.createModalOpen.set(false);
        this.refresh();
      },
      error: (err: { error?: { message?: string } }) => {
        this.createSaving.set(false);
        this.createError.set(err.error?.message ?? 'Could not create user.');
      },
    });
  }

  googleLinked(user: PlatformUserDto): boolean {
    const googleId = user.googleId?.trim();
    return !!googleId && !googleId.startsWith('pending:');
  }

  openEdit(user: PlatformUserDto): void {
    if (!this.canEdit()) return;
    this.editingUser.set(user);
    this.editName = user.name;
    this.editRoleKey =
      user.roleKey === 'SUPER_ADMIN' || user.roleKey === 'COUNSELLOR' || user.roleKey === 'PLATFORM_STAFF'
        ? user.roleKey
        : 'PLATFORM_STAFF';
    this.editError.set(null);
  }

  closeEdit(): void {
    if (this.editSaving()) return;
    this.editingUser.set(null);
  }

  confirmEdit(): void {
    if (!this.canEdit()) return;
    const user = this.editingUser();
    const name = this.editName.trim();
    if (!user) return;
    if (!name) {
      this.editError.set('Name is required.');
      return;
    }

    this.editSaving.set(true);
    this.editError.set(null);
    this.usersService.update(user._id, { name, roleKey: this.editRoleKey }).subscribe({
      next: () => {
        this.editSaving.set(false);
        this.editingUser.set(null);
        this.refresh();
        if (user.userId === this.currentUserId()) {
          void this.authService.restoreSession();
        }
      },
      error: (err: { error?: { message?: string } }) => {
        this.editSaving.set(false);
        this.editError.set(err.error?.message ?? 'Could not update user.');
      },
    });
  }

  canDeleteUser(user: PlatformUserDto): boolean {
    return this.canDeleteAction() && user.userId !== this.currentUserId();
  }

  showDeleteUser(user: PlatformUserDto): boolean {
    return this.showDelete() && user.userId !== this.currentUserId();
  }

  remove(user: PlatformUserDto): void {
    if (!this.canDeleteUser(user)) return;
    if (!confirm(`Delete ${user.name} (${user.email})? This cannot be undone.`)) return;
    this.usersService.remove(user._id).subscribe({
      next: () => this.refresh(),
      error: (err: { error?: { message?: string } }) => {
        alert(err.error?.message ?? 'Could not delete user.');
      },
    });
  }
}
